import React, { useState, useEffect, useRef } from 'react';
import { 
  Button, 
  Card, 
  Flex, 
  Heading, 
  Text, 
  View,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Alert,
  Loader,
  Icon
} from '@aws-amplify/ui-react';
import useAccessibilityFix from '../../hooks/useAccessibilityFix';
import corsProxy from '../../utils/corsProxy';
import { getStatusBadgeVariation } from '../../utils/helpers';

const ParticipantManagement = ({ project, onClose, onSave }) => {
  const [participants, setParticipants] = useState([]);
  const [newParticipant, setNewParticipant] = useState({ email: '', name: '', order: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Apply accessibility fixes
  useAccessibilityFix();
  const [csvUploadStatus, setCsvUploadStatus] = useState(null);
  const fileInputRef = useRef(null);
  const [assigningNumber, setAssigningNumber] = useState(null); // Track which participant is being assigned a number
  const [numberToAssign, setNumberToAssign] = useState(''); // The number being assigned
  const [createdCredentials, setCreatedCredentials] = useState([]); // Store temp passwords for new users
  const [showCredentials, setShowCredentials] = useState(false); // Show credentials modal

  useEffect(() => {
    // Load participants from multiple sources
    setLoading(true);
    
    const loadParticipants = async () => {
      try {
        const existingParticipants = [];
        
        // First, try to fetch participants from the API
        if (project && (project.projectId || project.id)) {
          try {
            const projectId = project.projectId || project.id;
            console.log('Fetching project data with participants for project:', projectId);

            const response = await corsProxy.get(`/projects/${projectId}`);
            
            console.log('Project data API response:', response);
            
            // Handle corsProxy response format and extract participant details
            let projectData = {};
            if (response.data) {
              projectData = response.data;
            } else {
              projectData = response;
            }
            
            // Extract participants from the project's participantDetails array
            let participantsData = [];
            if (projectData.participantDetails && Array.isArray(projectData.participantDetails)) {
              participantsData = projectData.participantDetails.map(participant => ({
                id: participant.email, // Use email as ID
                email: participant.email,
                name: participant.name,
                order: participant.order,
                status: participant.status,
                // Use actual selected number from database (null if not selected)
                selectedNumber: participant.selectedNumber === null || participant.selectedNumber === undefined ? null : participant.selectedNumber
              }));
            }
            
            console.log('All participants data from project:', participantsData);
            
            // Convert to the expected format for ParticipantManagement
            participantsData.forEach((participant, index) => {
              existingParticipants.push({
                id: participant.id || `participant-${index + 1}`,
                email: participant.email,
                name: participant.name,
                order: participant.order || index + 1,
                selectedNumber: participant.selectedNumber,
                status: participant.status || 'Pending'
              });
            });
            
            console.log('Converted participants from project data:', existingParticipants);
            
          } catch (apiError) {
            console.error('Error fetching project data:', apiError);
            console.log('Falling back to project.participantIds if available');
          }
        }
        
        // Fallback: Add participants from project.participantIds if they exist and we didn't get API data
        if (existingParticipants.length === 0 && project && project.participantIds) {
          project.participantIds.forEach((email, index) => {
            existingParticipants.push({
              id: `participant-${index + 1}`,
              email,
              name: email.split('@')[0],
              order: index + 1,
              selectedNumber: null,
              status: 'Pending'
            });
          });
        }
        
        // Check localStorage for any additional participants that were added in this session
        const sessionKey = `participants_${project?.projectId || project?.id}`;
        const sessionParticipants = localStorage.getItem(sessionKey);
        if (sessionParticipants) {
          try {
            const additionalParticipants = JSON.parse(sessionParticipants);
            additionalParticipants.forEach(participant => {
              // Only add if not already in the list
              if (!existingParticipants.some(p => p.email === participant.email)) {
                existingParticipants.push(participant);
              }
            });
          } catch (e) {
            console.error('Error parsing session participants:', e);
          }
        }
        
        console.log('Final participants list:', existingParticipants);
        setParticipants(existingParticipants);
        
      } catch (error) {
        console.error('Error loading participants:', error);
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadParticipants();
  }, [project]);

  const saveParticipantsToSession = (participantList) => {
    // Save participants to localStorage so they persist during the session
    const sessionKey = `participants_${project?.projectId || project?.id}`;
    const participantsToSave = participantList.filter(p => 
      // Only save participants that aren't already in project.participantIds
      !project?.participantIds?.includes(p.email)
    );
    localStorage.setItem(sessionKey, JSON.stringify(participantsToSave));
  };

  const createParticipantUser = async (email, name, projectId) => {
    try {
      const response = await corsProxy.post('/participant-users', {
        email,
        name,
        projectId,
        role: 'PARTICIPANT'
      });
      console.log('Participant user created:', response);
      return response;
    } catch (error) {
      console.error('Error creating participant user:', error);
      throw error;
    }
  };

  const handleAddParticipant = async () => {
    if (!newParticipant.email.trim()) {
      setError('Email is required');
      return;
    }

    // Check for duplicate email
    if (participants.some(p => p.email === newParticipant.email)) {
      setError('A participant with this email already exists');
      return;
    }

    const newOrder = participants.length > 0
      ? Math.max(...participants.map(p => p.order)) + 1
      : 1;

    const participant = {
      id: `participant-${Date.now()}`,
      email: newParticipant.email,
      name: newParticipant.name || newParticipant.email.split('@')[0],
      order: newOrder,
      selectedNumber: null,
      status: 'Pending'
    };

    // Add participant to the list (Cognito user creation deferred until email system is implemented)
    const updatedParticipants = [...participants, participant];
    setParticipants(updatedParticipants);
    saveParticipantsToSession(updatedParticipants);

    setNewParticipant({ email: '', name: '', order: 0 });
    setSuccess('Participant added. Click Save to persist changes.');

    // Clear messages after 3 seconds
    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 3000);
  };

  const handleRemoveParticipant = (id) => {
    const updatedParticipants = participants.filter(p => p.id !== id);
    setParticipants(updatedParticipants);
    saveParticipantsToSession(updatedParticipants);
    setSuccess('Participant removed successfully');
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleMoveParticipant = (id, direction) => {
    const currentIndex = participants.findIndex(p => p.id === id);
    if (currentIndex === -1) {
      return;
    }
    
    if (direction === 'up' && currentIndex === 0 || 
        direction === 'down' && currentIndex === participants.length - 1) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newParticipants = [...participants];
    
    // Swap participants
    [newParticipants[currentIndex], newParticipants[newIndex]] = 
    [newParticipants[newIndex], newParticipants[currentIndex]];
    
    // Update order numbers
    newParticipants.forEach((p, index) => {
      p.order = index + 1;
    });
    
    setParticipants(newParticipants);
    saveParticipantsToSession(newParticipants);
  };

  const handleResendInvitation = (id) => {
    // In a real app, you would call an API to resend the invitation
    setSuccess(`Invitation resent to participant ${id}`);
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleAssignNumber = (participantId) => {
    setAssigningNumber(participantId);
    setNumberToAssign('');
  };

  const handleConfirmNumberAssignment = () => {
    const number = parseInt(numberToAssign, 10);
    const minNumber = project.numberRange?.min || 1;
    const maxNumber = project.numberRange?.max || 100;

    // Validate number
    if (isNaN(number) || number < minNumber || number > maxNumber) {
      setError(`Please enter a valid number between ${minNumber} and ${maxNumber}`);
      return;
    }

    // Check if number is already taken
    const isNumberTaken = participants.some(p => p.selectedNumber === number && p.id !== assigningNumber);
    if (isNumberTaken) {
      setError(`Number ${number} is already assigned to another participant`);
      return;
    }

    // Update participant with assigned number
    setParticipants(prev => prev.map(p => 
      p.id === assigningNumber 
        ? { ...p, selectedNumber: number, status: 'Completed' }
        : p
    ));

    setAssigningNumber(null);
    setNumberToAssign('');
    setSuccess(`Number ${number} assigned successfully`);
  };

  const handleCancelNumberAssignment = () => {
    setAssigningNumber(null);
    setNumberToAssign('');
  };
  
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is CSV
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setCsvUploadStatus('Processing CSV...');
    console.log('CSV upload started');
    
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n');
        
        console.log('CSV content:', csv);
        console.log('CSV lines:', lines);
        console.log('Number of lines:', lines.length);
        
        if (lines.length <= 1) {
          setError('CSV file is empty or contains only headers');
          setCsvUploadStatus(null);
          return;
        }
        
        // Get headers from first line
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        console.log('CSV headers:', headers);
        
        // Find column indices
        const emailIndex = headers.findIndex(h => h === 'email');
        const nameIndex = headers.findIndex(h => h === 'name' || h === 'fullname' || h === 'full name');
        const orderIndex = headers.findIndex(h => h === 'order' || h === 'sequence' || h === 'position');
        
        console.log('Column indices - email:', emailIndex, 'name:', nameIndex, 'order:', orderIndex);
        
        if (emailIndex === -1) {
          setError('CSV file must contain an "email" column');
          setCsvUploadStatus(null);
          return;
        }
        
        // Skip header row and filter out empty lines
        const validLines = lines.slice(1).filter(line => line.trim() !== '');
        
        if (validLines.length === 0) {
          setError('CSV file contains no participant data');
          setCsvUploadStatus(null);
          return;
        }

        const newParticipants = validLines.map((line, index) => {
          // Split by comma, but handle quoted values (which might contain commas)
          const values = line.split(',').map(val => val.trim());
          console.log(`Line ${index + 1} values:`, values);
          
          const email = values[emailIndex];
          const name = nameIndex !== -1 ? values[nameIndex] : '';
          const order = orderIndex !== -1 ? parseInt(values[orderIndex], 10) || (index + 1) : (index + 1);
          
          return {
            id: `participant-${Date.now()}-${index}`,
            email,
            name: name || email.split('@')[0],
            order: order,
            selectedNumber: null,
            status: 'Invited'
          };
        });

        console.log('Parsed participants:', newParticipants);

        // Filter out participants with empty emails
        const validParticipants = newParticipants.filter(p => p.email.trim() !== '');
        
        if (validParticipants.length === 0) {
          setError('No valid participants found in CSV file');
          setCsvUploadStatus(null);
          return;
        }

        // Sort by order
        validParticipants.sort((a, b) => a.order - b.order);
        
        // Update order numbers to be sequential
        validParticipants.forEach((p, index) => {
          p.order = index + 1;
        });

        // Create Cognito user accounts for all participants
        setLoading(true);
        setCsvUploadStatus('Creating user accounts...');
        
        try {
          // Process participants in batches to avoid overwhelming the API
          const batchSize = 5;
          const batches = [];
          
          for (let i = 0; i < validParticipants.length; i += batchSize) {
            batches.push(validParticipants.slice(i, i + batchSize));
          }
          
          let successCount = 0;
          let errorCount = 0;
          
          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            const batchPromises = batch.map(participant => 
              createParticipantUser(
                participant.email, 
                participant.name, 
                project.projectId || project.id
              ).then(() => {
                successCount++;
                return true;
              }).catch(error => {
                console.error(`Error creating user for ${participant.email}:`, error);
                errorCount++;
                return false;
              })
            );
            
            await Promise.all(batchPromises);
            
            // Update status after each batch
            setCsvUploadStatus(`Processing... ${successCount + errorCount}/${validParticipants.length} participants`);
          }
          
          // Set final status
          if (errorCount > 0) {
            setCsvUploadStatus(`Imported ${validParticipants.length} participants. Created ${successCount} user accounts (${errorCount} failed)`);
            setSuccess(`Imported ${validParticipants.length} participants. Created ${successCount} user accounts (${errorCount} failed)`);
          } else {
            setCsvUploadStatus(`Successfully imported ${validParticipants.length} participants and created user accounts`);
            setSuccess(`Successfully imported ${validParticipants.length} participants and created user accounts`);
          }
        } catch (error) {
          console.error('Error creating user accounts:', error);
          setCsvUploadStatus(`Imported participants but encountered errors creating user accounts`);
          setSuccess(`Imported participants but encountered errors creating user accounts`);
        } finally {
          setLoading(false);
        }

        setParticipants(validParticipants);
        saveParticipantsToSession(validParticipants);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (err) {
        console.error('Error parsing CSV:', err);
        setError('Error parsing CSV file. Please check the format.');
        setCsvUploadStatus(null);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading the CSV file');
      setCsvUploadStatus(null);
    };
    
    reader.readAsText(file);
  };

  const handleSave = async (event) => {
    // Prevent default form submission behavior
    if (event) {
      event.preventDefault();
    }

    console.log("ParticipantManagement - handleSave called");
    console.log("Participants to save:", participants);
    console.log("Project:", project);

    // Remove any aria-hidden attributes from the document root
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.removeAttribute('aria-hidden');
    }

    // Remove aria-hidden from any element containing focus
    document.querySelectorAll('[aria-hidden="true"]').forEach(el => {
      if (el.contains(document.activeElement)) {
        el.removeAttribute('aria-hidden');
      }
    });

    // Clear session storage when saving
    const sessionKey = `participants_${project?.projectId || project?.id}`;
    localStorage.removeItem(sessionKey);

    setLoading(true);
    setError(null);

    try {
      const projectId = project?.projectId || project?.id;
      const credentials = [];

      // Create Cognito users for each participant
      for (const participant of participants) {
        try {
          console.log(`Creating Cognito user for: ${participant.email}`);
          const response = await createParticipantUser(
            participant.email,
            participant.name,
            projectId
          );

          // If a new user was created, store the temp password
          if (response && !response.isExistingUser && response.tempPassword) {
            credentials.push({
              email: participant.email,
              name: participant.name,
              tempPassword: response.tempPassword
            });
          }
        } catch (err) {
          console.error(`Error creating user for ${participant.email}:`, err);
          // Continue with other participants even if one fails
        }
      }

      // If we created new users, show the credentials
      if (credentials.length > 0) {
        setCreatedCredentials(credentials);
        setShowCredentials(true);
      }

      // Call onSave to persist participants to the project
      console.log("About to call onSave with participants");
      onSave(participants);
      console.log("onSave called");

      if (credentials.length === 0) {
        setSuccess("Participants saved successfully!");
      } else {
        setSuccess(`Participants saved! ${credentials.length} new user account(s) created.`);
      }
    } catch (error) {
      console.error("Error saving participants:", error);
      setError("Failed to save participants. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    return <Badge variation={getStatusBadgeVariation(status)}>{status}</Badge>;
  };

  return (
    <Card variation="elevated" padding="1.5rem">
      <Flex direction="column" gap="1.5rem">
        
        <Flex justifyContent="space-between" alignItems="center">
          <Heading level={3}>Manage Participants - {project.name}</Heading>
          <Button variation="link" onClick={onClose}>
            Back to Projects
          </Button>
        </Flex>

        {error && (
          <Alert variation="error" isDismissible={true} onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variation="success" isDismissible={true} onDismiss={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Credentials Display - Show temp passwords for new users */}
        {showCredentials && createdCredentials.length > 0 && (
          <Card variation="elevated" padding="1.5rem" backgroundColor="yellow.10">
            <Flex direction="column" gap="1rem">
              <Flex justifyContent="space-between" alignItems="center">
                <Heading level={4}>New User Credentials</Heading>
                <Button
                  variation="link"
                  onClick={() => {
                    setShowCredentials(false);
                    setCreatedCredentials([]);
                  }}
                >
                  Dismiss
                </Button>
              </Flex>
              <Alert variation="warning">
                <Text fontWeight="bold">Important: Save these credentials now!</Text>
                <Text>Since email notifications are not yet configured, please manually share these login credentials with participants. They will need to change their password on first login.</Text>
              </Alert>
              <Table highlightOnHover={true}>
                <TableHead>
                  <TableRow>
                    <TableCell as="th">Name</TableCell>
                    <TableCell as="th">Email (Username)</TableCell>
                    <TableCell as="th">Temporary Password</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {createdCredentials.map((cred, index) => (
                    <TableRow key={index}>
                      <TableCell>{cred.name}</TableCell>
                      <TableCell>{cred.email}</TableCell>
                      <TableCell>
                        <code style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                          {cred.tempPassword}
                        </code>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Text fontSize="0.85rem" color="gray">
                Login URL: {window.location.origin}
              </Text>
            </Flex>
          </Card>
        )}

        <Card variation="outlined" padding="1rem">
          <Heading level={5}>Add New Participant</Heading>
          <Flex gap="1rem" marginTop="1rem" alignItems="flex-end">
            <TextField
              label="Email"
              value={newParticipant.email}
              onChange={e => setNewParticipant({...newParticipant, email: e.target.value})}
              required
            />
            
            <TextField
              label="Name (Optional)"
              value={newParticipant.name}
              onChange={e => setNewParticipant({...newParticipant, name: e.target.value})}
            />
            
            <Button onClick={handleAddParticipant}>
              Add Participant
            </Button>
          </Flex>
        </Card>
        
        {/* Only show CSV import if no participants exist or if CSV import is enabled in project settings */}
        {(participants.length === 0 || project.allowCsvImport) && (
          <Card variation="outlined" padding="1rem">
            <Heading level={5}>Import Participants from CSV</Heading>
            <Text fontSize="0.9rem" color="gray">
              Upload a CSV file with columns: First Name, Last Name, Email, Selection Order
            </Text>
            <Flex gap="1rem" marginTop="1rem" alignItems="center">
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                ref={fileInputRef}
                style={{ display: 'none' }}
                id="csv-upload"
              />
              <Button 
                onClick={() => document.getElementById('csv-upload').click()}
                variation="primary"
              >
                Select CSV File
              </Button>
              {csvUploadStatus && (
                <Text fontSize="0.9rem" color={csvUploadStatus.includes('Success') ? 'green' : 'gray'}>
                  {csvUploadStatus}
                </Text>
              )}
              
              <Button 
                variation="link" 
                onClick={() => {
                  // Create a sample CSV with header row
                  const csvContent = "First Name,Last Name,Email,Selection Order\nJohn,Doe,john.doe@example.com,1\nJane,Smith,jane.smith@example.com,2";
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'sample_participants.csv';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                Download Sample CSV
              </Button>
            </Flex>
          </Card>
        )}

        <Card variation="outlined" padding="1rem">
          <Heading level={5}>Participants</Heading>
          
          {loading ? (
            <Flex justifyContent="center" padding="2rem">
              <Loader size="large" />
              <Text padding="0.5rem">Loading participants...</Text>
            </Flex>
          ) : participants.length > 0 ? (
            <Table highlightOnHover={true}>
              <TableHead>
                <TableRow>
                  <TableCell as="th">Order</TableCell>
                  <TableCell as="th">Name</TableCell>
                  <TableCell as="th">Email</TableCell>
                  <TableCell as="th">Status</TableCell>
                  <TableCell as="th">Selected Number</TableCell>
                  <TableCell as="th">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {participants.map((participant) => (
                  <TableRow key={participant.id}>
                    <TableCell>{participant.order}</TableCell>
                    <TableCell>{participant.name}</TableCell>
                    <TableCell>{participant.email}</TableCell>
                    <TableCell>{getStatusBadge(participant.status)}</TableCell>
                    <TableCell>
                      {assigningNumber === participant.id ? (
                        <Flex gap="0.5rem" alignItems="center">
                          <TextField
                            size="small"
                            type="number"
                            value={numberToAssign}
                            onChange={(e) => setNumberToAssign(e.target.value)}
                            placeholder={`${project.numberRange?.min || 1}-${project.numberRange?.max || 100}`}
                            width="80px"
                          />
                          <Button size="small" onClick={handleConfirmNumberAssignment}>
                            ✓
                          </Button>
                          <Button size="small" onClick={handleCancelNumberAssignment}>
                            ✗
                          </Button>
                        </Flex>
                      ) : (
                        <Flex gap="0.5rem" alignItems="center">
                          <Text>{participant.selectedNumber || 'Not selected'}</Text>
                          {!participant.selectedNumber && (
                            <Button 
                              size="small" 
                              variation="link"
                              onClick={() => handleAssignNumber(participant.id)}
                            >
                              Assign
                            </Button>
                          )}
                        </Flex>
                      )}
                    </TableCell>
                    <TableCell>
                      <Flex gap="0.5rem">
                        <Button 
                          size="small" 
                          onClick={() => handleMoveParticipant(participant.id, 'up')}
                          isDisabled={participant.order === 1}
                        >
                          ↑
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => handleMoveParticipant(participant.id, 'down')}
                          isDisabled={participant.order === participants.length}
                        >
                          ↓
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => handleResendInvitation(participant.id)}
                        >
                          Resend
                        </Button>
                        <Button 
                          size="small" 
                          variation="destructive" 
                          onClick={() => handleRemoveParticipant(participant.id)}
                        >
                          Remove
                        </Button>
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Text textAlign="center" padding="1rem">
              No participants added yet. Add participants using the form above or import from CSV.
            </Text>
          )}
        </Card>
        
        <Flex justifyContent="space-between">
          <Button onClick={onClose}>
            Cancel
          </Button>
          
          <Button 
            variation="primary" 
            onClick={handleSave}
            isDisabled={participants.length === 0}
          >
            Save Participants
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};

export default ParticipantManagement;
